/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect } from 'react';

// Mantine
import { Box, Title } from '@mantine/core';

// Components
import { Select } from '@components/atoms/Select';
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './AddApiTokenForm.module.css';

// Utils
import {
	cleanObject,
	convertArrayToLabelValue,
} from '@utils/functions';

// APIs
import { useOrganizationsByProducers } from 'shared/api/get-organizations-by-producers';
import { useOrganizations } from 'shared/api/get-organizations';
import { useEntities } from 'shared/api/get-entities';

// Icons
import useStore from '@store/index';

// I18n
import { TextInput } from '@components/atoms/TextInput';
import { DatePicker } from '@components/atoms/DatePicker';
import { useAddApiToken } from '@features/resources/api-tokens/api/create-api-token';
import { filterEntitiesByIDs } from '@features/resources/api-tokens/utils/functions';
import { useTranslation } from 'react-i18next';
import { Divider, TextInputV2 } from '@components/atoms';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import { useMediaQuery } from '@mantine/hooks';
import { TProducer } from '@global-types/api/api';
import { UseFormReturnType } from '@mantine/form';
import { TFormValuesApiToken } from '@features/resources/api-tokens/types';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddApiTokenForm = (props: {
	form: UseFormReturnType<TFormValuesApiToken>;
	onCancel: () => void;
	onBack: () => void;
}) => {
	const {
		apiTokensPage,
		apiTokensPageSize,
		apiTokenOrganization,
		setApiTokenOrganization,
	} = useStore();

	const { t } = useTranslation();

	const { data: organizations } = useOrganizations();
	const { data: entities } = useEntities({
		organization: apiTokenOrganization,
	});

	const { data: organizationsByProducersIDs } =
		useOrganizationsByProducers();

	const addApiToken = useAddApiToken(
		apiTokensPage,
		apiTokensPageSize
	);

	useEffect(() => {
		if (addApiToken.isSuccess) {
			props.onBack();
		}
	}, [addApiToken.isSuccess, props]);

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const handleSubmitForm = () => {
			props.form.validate();
			if (props.form.isValid()) {
				const {
					name,
					organization,
					entity,
					expiredAt,
					domain,
				} = props.form.values;

				const newExpiredDate =
					dayjs(expiredAt).format('YYYY-MM-DD');

				addApiToken.mutate(
					cleanObject({
						name,
						organization,
						entity,
						expiredAt: !isNil(expiredAt)
							? new Date(newExpiredDate)
							: null,
						domain,
					})
				);
			}
		};

		const {
			onChange: onChangeProject,
			...organizationProps
		} = props.form.getInputProps('organization');

		const handleChangeOrganization = (_id: string) => {
			props.form.setFieldValue('organization', _id);
			if (_id) setApiTokenOrganization(_id);
			props.form.setFieldValue('entity', null);
		};

		const { onChange: onChangeSite, ...entityProps } =
			props.form.getInputProps('entity');

		const handleChangeEntity = (value: string) => {
			props.form.setFieldValue('entity', value);
		};
		/* ******************************** Media Query ******************************* */
		const isBigScreen = useMediaQuery(
			'(min-width: 120em)'
		);
		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(() => {})}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						API Tokens
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('apiTokens.addApiTokens')}
					</Text>
				</Box>
				{/* Row for Name */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInputV2
							visibleFrom="xxl"
							size="md"
							label={t('global.name')}
							withAsterisk
							placeholder={t('global.nameMincharacter')}
							{...props.form.getInputProps('name')}
						/>
						<TextInputV2
							hiddenFrom="xxl"
							size="sm"
							label={t('global.name')}
							withAsterisk
							placeholder={t('global.nameMincharacter')}
							{...props.form.getInputProps('name')}
						/>
					</Box>
				</Box>
				{/* Row for Organization & Entity */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							visibleFrom="xxl"
							size="md"
							label={t('global.organization')}
							withAsterisk
							data={convertArrayToLabelValue(
								filterEntitiesByIDs(
									organizations,
									organizationsByProducersIDs
								)
							)}
							placeholder={t('global.selectOrganization')}
							onChange={handleChangeOrganization}
							{...organizationProps}
						/>
						<Select
							hiddenFrom="xxl"
							size="sm"
							label={t('global.organization')}
							withAsterisk
							data={convertArrayToLabelValue(
								filterEntitiesByIDs(
									organizations,
									organizationsByProducersIDs
								)
							)}
							placeholder={t('global.selectOrganization')}
							onChange={handleChangeOrganization}
							{...organizationProps}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<Select
							disabled={
								!props.form.isValid('organization')
							}
							visibleFrom="xxl"
							size="md"
							label={t('global.entity')}
							withAsterisk
							data={convertArrayToLabelValue(
								entities.filter(
									(item: TProducer) =>
										item.organization ==
										props.form.getInputProps(
											'organization'
										).value
								)
							)}
							placeholder={t('global.selectEntity')}
							onChange={handleChangeEntity}
							{...entityProps}
						/>
						<Select
							disabled={
								!props.form.isValid('organization')
							}
							hiddenFrom="xxl"
							size="sm"
							label={t('global.entity')}
							withAsterisk
							data={convertArrayToLabelValue(
								entities.filter(
									(item: TProducer) =>
										item.organization ==
										props.form.getInputProps(
											'organization'
										).value
								)
							)}
							placeholder={t('global.selectEntity')}
							onChange={handleChangeEntity}
							{...entityProps}
						/>
					</Box>
				</Box>
				<Divider size="xs" />
				{/* Row for Domain & Expiration date */}

				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInput
							visibleFrom="xxl"
							size="md"
							label={t('global.domain')}
							placeholder="sub.example.com"
							{...props.form.getInputProps('domain')}
						/>
						<TextInput
							hiddenFrom="xxl"
							size="sm"
							label={t('global.domain')}
							placeholder="sub.example.com"
							{...props.form.getInputProps('domain')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<DatePicker
							clearable
							visibleFrom="xxl"
							size="lg"
							label={t('global.expirationDate')}
							placeholder={t('global.selectDate')}
							{...props.form.getInputProps('expiredAt')}
						/>
						<DatePicker
							clearable
							hiddenFrom="xxl"
							size="sm"
							label={t('global.expirationDate')}
							placeholder={t('global.selectDate')}
							{...props.form.getInputProps('expiredAt')}
						/>
					</Box>
				</Box>
				<Divider size="xs" />
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							hiddenFrom="xxl"
							size="md"
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
						<Button
							fullWidth
							size="lg"
							variant="transparent"
							visibleFrom="xxl"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={handleSubmitForm}
							loading={addApiToken.isLoading}
						>
							{t('apiTokens.addApiTokens')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
